import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { isLoggedIn } from "../utils/auth";
import { buildLoginUrl } from "../utils/authRedirect";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function fmtMoney(cents, currency = "EUR") {
  const v = (Number(cents || 0) / 100).toFixed(2);
  return `${v} ${String(currency).toUpperCase()}`;
}
function fmtDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function PaymentReceiptPage() {
  const { paymentId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/api/payments/${paymentId}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (res.status === 401 || res.status === 403) {
          // Non loggato / token scaduto: login e poi ritorno qui (?redirect=).
          // Loggato ma senza accesso (403): login semplice come prima, che
          // porta alla home -- con il redirect tornerebbe qui in loop.
          navigate(isLoggedIn() ? "/login" : buildLoginUrl(location));
          return;
        }
        const json = await res.json().catch(() => ({}));
        if (!res.ok) setErr(json.error || "Impossibile caricare la ricevuta");
        else if (alive) setData(json);
      } catch {
        setErr("Errore di rete");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId, navigate]);

  return (
    <div style={st.page}>
      <div style={st.topbar}>
        <div style={st.topbarInner}>
          <img
            src="/logo.png"
            alt="StirUp logo"
            style={st.logo}
            width={64}
            height={64}
          />
          <h1 style={st.topbarTitle}>
            STIRUP: PIATTAFORMA DI STIRO CON RITIRO E CONSEGNA A DOMICILIO
          </h1>
        </div>
      </div>

      <div style={st.hero}>
        <div style={st.heroInner}>
          <div style={st.card}>
            <h2 style={st.cardTitle}>Ricevuta pagamento</h2>
            <p style={st.cardSubtitle}>
              ID pagamento: <strong>{paymentId}</strong>
            </p>

            <div style={st.fieldsFrame}>
              {loading ? (
                <p>Caricamento…</p>
              ) : err ? (
                <div role="alert" style={st.errorBox}>
                  {err}
                </div>
              ) : data ? (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <div>
                      <strong>Data:</strong> {fmtDate(data.created_ms)}
                    </div>
                    <div>
                      <strong>Stato:</strong>{" "}
                      {String(data.status || "").toUpperCase()}
                    </div>
                    <div>
                      <strong>Totale:</strong>{" "}
                      {fmtMoney(data.amount_total_cents, data.currency)}
                    </div>
                    {data.discount_total_cents ? (
                      <div>
                        <strong>Sconto:</strong>{" "}
                        {fmtMoney(data.discount_total_cents, data.currency)}
                      </div>
                    ) : null}
                  </div>

                  <h3 style={{ margin: "14px 0 8px", fontSize: 16 }}>
                    Dettagli ordine
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {(data.items || []).map((it, idx) => (
                      <li key={idx} style={{ marginBottom: 6 }}>
                        {it.description} × {it.quantity} —{" "}
                        {fmtMoney(it.amount_total_cents, data.currency)}
                      </li>
                    ))}
                  </ul>

                  {(data.prenotazioni || []).length ? (
                    <>
                      <h3 style={{ margin: "16px 0 8px", fontSize: 16 }}>
                        Prenotazioni create
                      </h3>
                      <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {data.prenotazioni.map((p) => (
                          <li key={p.id}>
                            Prenotazione #{p.id} — Totale:{" "}
                            {fmtMoney(p.totale_cents, data.currency)}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  <div style={st.actions}>
                    <Link
                      to="/dashboard"
                      className="btn-outline"
                      style={st.btnOutline}
                    >
                      Torna alla dashboard
                    </Link>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <style>{`.btn-outline:hover { background: #fff9cc; }`}</style>

      <footer style={st.footer}>
        <p style={st.payoff}>
          Stirup: piattaforma di stiro con ritiro e consegna a domicilio
        </p>
      </footer>
    </div>
  );
}

const ORANGE = "#FFD400";
const st = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
  },
  topbar: { background: "#fff", borderBottom: "1px solid #eee" },
  topbarInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logo: { display: "block", height: 64, width: 64, objectFit: "contain" },
  topbarTitle: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.2,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".3px",
    color: "#0b0b0b",
  },
  hero: { background: "#AEE3FA" },
  heroInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "80px 16px 100px",
    display: "flex",
    justifyContent: "center",
  },
  card: {
    width: "min(720px, 92vw)",
    background: "#fff",
    borderRadius: 8,
    padding: "36px 32px",
    minHeight: 420,
    boxShadow: "0 6px 20px rgba(0,0,0,.08)",
    border: "1px solid #e9e9e9",
    textAlign: "left",
  },
  cardTitle: {
    margin: "8px 0 10px",
    fontSize: 22,
    fontWeight: 700,
    color: "#222",
  },
  cardSubtitle: { margin: "0 0 22px", fontSize: 14, color: "#666" },
  fieldsFrame: {
    border: "1px solid #e8e8e8",
    borderRadius: 8,
    padding: "18px 16px",
  },
  errorBox: {
    marginTop: 16,
    padding: "10px 12px",
    background: "#fff2f2",
    border: "1px solid #ffd6d6",
    color: "#a40000",
    borderRadius: 8,
    textAlign: "center",
    fontSize: 14,
  },
  actions: { marginTop: 18, display: "flex", justifyContent: "center" },
  btnOutline: {
    appearance: "none",
    borderRadius: 999,
    border: `2px solid ${ORANGE}`,
    color: ORANGE,
    background: "transparent",
    fontSize: 15,
    fontWeight: 700,
    padding: "12px 28px",
    cursor: "pointer",
    textTransform: "uppercase",
    textDecoration: "none",
  },
  footer: {
    maxWidth: 1100,
    margin: "18px auto 24px",
    padding: "0 16px",
    textAlign: "center",
  },
  payoff: { margin: 0, fontSize: 16, color: "#222" },
};
